/**
 * Editor — デュアルエディタ（CM6 / textarea）の統合コンポーネント（docs/03-editor.md §1）。
 *
 * この実装が守っている IME 規律:
 * 1. **本文を React state に持たない。** 値は `valueRef` に退避するだけで、描画に使わない。
 *    state 更新で子（textarea / CM6）が再描画されないようにするため。
 * 2. **子は `memo` で完全に隔離**（比較関数が常に true）。親がどれだけ再描画されても
 *    エディタ本体の DOM は触られない。ステータスチップ等の更新で composition が
 *    壊れない（docs/03 §7 チェックリスト 9）。
 * 3. **エディタ切り替えは `key` を変えた再マウント。** 現在値を `valueRef` から引き継ぐ。
 *    切り替えは「同じ文字列での再マウント」に過ぎない（docs/03 §1）。
 * 4. `onChange` は composition 中も発火する。ドラフトバッファへの退避は変換中も行う
 *    ため（docs/03 §4-5）。**サーバー送信の可否は親が `adapter.isComposing()` で判断する。**
 * 5. 親から渡される callback は ref に退避し、子へ渡す props は常に安定参照にする。
 */

import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { CodeMirrorEditor } from './CodeMirrorEditor';
import { TextareaEditor } from './TextareaEditor';
import { AccessoryBar } from './AccessoryBar';
import type { EditorAdapter, EditorKind } from './EditorAdapter';
import './editor.css';

/** エディタ選択の記憶先（端末ごと。docs/03 §1）。 */
export const EDITOR_KIND_STORAGE_KEY = 'mamemo6.editor.kind';

const DEFAULT_EDITOR_KIND: EditorKind = 'codemirror';

export function loadEditorKind(): EditorKind {
  try {
    const raw = localStorage.getItem(EDITOR_KIND_STORAGE_KEY);
    return raw === 'textarea' || raw === 'codemirror' ? raw : DEFAULT_EDITOR_KIND;
  } catch {
    // プライベートモード等で localStorage が使えなくても書けなくならないようにする。
    return DEFAULT_EDITOR_KIND;
  }
}

function saveEditorKind(kind: EditorKind): void {
  try {
    localStorage.setItem(EDITOR_KIND_STORAGE_KEY, kind);
  } catch {
    /* 記憶できなくても編集は続行する */
  }
}

export interface EditorProps {
  /** 初期本文。マウント後の変更は反映しない（差し替えは呼び出し側で `key` を変える）。 */
  initialValue: string;
  /** 値が変わったことの通知。composition 中も呼ばれる。 */
  onChange?: (value: string) => void;
  /** エディタ接続時に adapter を、切断時に null を渡す。切り替え時は両方呼ばれる。 */
  onEditorReady?: (adapter: EditorAdapter | null) => void;
  /** アクセサリバーの「画像」ボタン。 */
  onRequestImage?: () => void;
  /** 切り出しボタン。選択が空なら null が渡る。 */
  onRequestExtract?: (selection: { start: number; end: number } | null) => void;
  autoFocus?: boolean;
  placeholder?: string;
  /** アクセサリバーを表示するか（既定 true）。 */
  showAccessoryBar?: boolean;
  /** アクセサリバーを visualViewport 追従の固定表示にするか（既定 true）。 */
  followKeyboard?: boolean;
  /** エディタ切り替えボタンを**エディタ自身が**表示するか（既定 true）。 */
  showSwitcher?: boolean;
  /**
   * 切り替え UI を呼び出し側へ出したいときに使う（編集画面はヘッダへ置く）。
   * マウント時と切り替え後に、現在の種類と切り替え関数を渡す。
   * `showSwitcher={false}` と併用する。
   */
  onSwitcherChange?: (state: EditorSwitcher) => void;
  className?: string;
}

/** 呼び出し側がヘッダ等へ切り替えボタンを置くための材料。 */
export interface EditorSwitcher {
  kind: EditorKind;
  /** 変換中は何もしない（IME 規律）。 */
  toggle: () => void;
}

function EditorImpl({
  initialValue,
  onChange,
  onEditorReady,
  onRequestImage,
  onRequestExtract,
  autoFocus,
  placeholder,
  showAccessoryBar = true,
  followKeyboard = true,
  showSwitcher = true,
  onSwitcherChange,
  className,
}: EditorProps) {
  const [kind, setKind] = useState<EditorKind>(() => loadEditorKind());

  // IME 規律 1: 本文は ref。描画には使わない。
  const valueRef = useRef(initialValue);
  const adapterRef = useRef<EditorAdapter | null>(null);
  const unsubscribeRef = useRef<(() => void) | null>(null);

  // IME 規律 5: 親の callback は ref に退避し、子へ渡す props を安定させる。
  const callbacksRef = useRef({ onChange, onEditorReady, onSwitcherChange });
  callbacksRef.current = { onChange, onEditorReady, onSwitcherChange };

  // 切り替え直後にフォーカスを戻すためのフラグ。
  const refocusRef = useRef(false);

  const handleReady = useCallback((adapter: EditorAdapter | null) => {
    // 前のエディタの購読を解除する。
    unsubscribeRef.current?.();
    unsubscribeRef.current = null;
    adapterRef.current = adapter;

    if (adapter) {
      valueRef.current = adapter.getValue();
      unsubscribeRef.current = adapter.onChange(() => {
        // composition 中も呼ばれる。ドラフトバッファ用（docs/03 §4-5）。
        valueRef.current = adapter.getValue();
        callbacksRef.current.onChange?.(valueRef.current);
      });
      if (refocusRef.current) {
        refocusRef.current = false;
        adapter.focus();
      }
    }
    callbacksRef.current.onEditorReady?.(adapter);
  }, []);

  useEffect(() => {
    return () => {
      unsubscribeRef.current?.();
      unsubscribeRef.current = null;
    };
  }, []);

  const toggleKind = useCallback(() => {
    const adapter = adapterRef.current;
    // IME 規律: 変換中の再マウントは composition を破壊する。確定してから切り替える。
    if (adapter?.isComposing()) return;
    // 現在値を確実に引き継ぐ（アンマウント前に読む）。
    if (adapter) valueRef.current = adapter.getValue();
    refocusRef.current = adapter != null && document.activeElement != null;
    setKind((prev) => {
      const next: EditorKind = prev === 'codemirror' ? 'textarea' : 'codemirror';
      saveEditorKind(next);
      return next;
    });
  }, []);

  // 切り替え UI を外へ出す場合の通知。マウント時と kind の変化時に呼ぶ。
  // **エディタ本体は再描画されない**（この effect は Editor 自身の state 変化でしか動かず、
  // 親が再描画されても memo が遮る）。
  useEffect(() => {
    callbacksRef.current.onSwitcherChange?.({ kind, toggle: toggleKind });
  }, [kind, toggleKind]);

  // key に kind を含めることで、切り替え＝再マウントになる。
  const surface =
    kind === 'codemirror' ? (
      <CodeMirrorEditor
        key="cm"
        initialValue={valueRef.current}
        onReady={handleReady}
        autoFocus={autoFocus}
        placeholder={placeholder}
      />
    ) : (
      <TextareaEditor
        key="ta"
        initialValue={valueRef.current}
        onReady={handleReady}
        autoFocus={autoFocus}
        placeholder={placeholder}
      />
    );

  return (
    <div className={className ? `mm-editor ${className}` : 'mm-editor'}>
      {showSwitcher && (
        <div className="mm-editor__switch">
          <span>{kind === 'codemirror' ? 'CodeMirror' : 'textarea'}</span>
          <button
            type="button"
            className="mm-editor__switch-button"
            // フォーカスを奪わない（変換中の誤爆と composition 中断を防ぐ）。
            onPointerDown={(ev) => ev.preventDefault()}
            onClick={toggleKind}
          >
            {kind === 'codemirror' ? 'textarea へ切り替え' : 'CodeMirror へ切り替え'}
          </button>
        </div>
      )}
      <div className="mm-editor__surface">{surface}</div>
      {showAccessoryBar && (
        <AccessoryBar
          adapterRef={adapterRef}
          onRequestImage={onRequestImage}
          {...(onRequestExtract ? { onRequestExtract } : {})}
          followKeyboard={followKeyboard}
        />
      )}
    </div>
  );
}

/**
 * IME 規律 2: 親の再描画からエディタを隔離する。
 * props は「マウント時に固定」として扱うため、比較関数は常に true を返す。
 * 本文や設定を差し替えたい場合は、呼び出し側で `key` を変えて再マウントすること。
 */
export const Editor = memo(EditorImpl, () => true);
Editor.displayName = 'Editor';
